import { FC } from 'react'
import { Box, Typography } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { latestTheme } from '../../shared/styles/theme'
import { colorTokens } from '../../shared/styles/colorTokens'
import { LAYOUT } from '../../shared/styles/spacing'
import { typeScale } from '../../shared/styles/typography'
import { GuideVariant } from './core'
import { GuideToc, TOC_WIDTH } from './components/GuideToc'
import { GuideRouter } from './components/GuideRouter'
import { MarkdownBody } from './components/MarkdownBody'

/**
 * まもる君 トラブルガイド（/guide とその系統別variant）
 *
 * - 未認証で開ける（constants/publicPaths）。「ログインできない」区分を含むため。
 * - 紙のA4シート裏面のURL/QR、およびまもる君web内のヘルプから来る。
 * - アプリのシェル（Sidebar/TopBar）は被せない。ログイン前後どちらでも、
 *   スマホでも同じ1枚として開けることを優先する。
 * - 系統別のコンテンツと連絡先は props（GuideVariant）で受ける。
 *   どの系統に何を出すかは routes.tsx と variants/ が持つ。
 *
 * 28 spec: 罫線で区切らない (spec §「section 間 divider なし。spacing と heading のみで分離」)。
 * 見出しは title-page(40) → 区分 h2(24) → 手順 h4(16) の3段のみ。
 * 区分の上に 64px、手順の上に 32px と、余白を倍で振って境目を作る。
 * 区分を h3(20) に置くと手順(16)との差が 4px しかなく、境目が見えなくなる。
 */
export const GuidePage: FC<GuideVariant> = ({ sections, contact }) => {
  return (
    <ThemeProvider theme={latestTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colorTokens.bg.default,
          px: { xs: 2, sm: 3 },
          py: { xs: 4, md: 5 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 6,
            justifyContent: 'center',
            alignItems: 'flex-start',
            mx: 'auto',
            maxWidth: LAYOUT.contentMax + TOC_WIDTH + 48,
          }}
        >
          <GuideToc sections={sections} />

          <Box sx={{ width: '100%', maxWidth: LAYOUT.contentMax, minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{ ...typeScale.titlePage, mb: 1, wordBreak: 'keep-all' }}
            >
              まもる君 困ったときガイド
            </Typography>
            <Typography sx={{ ...typeScale.body, color: colorTokens.fg.muted }}>
              処方箋がどこまで進んだかで選んでください。
            </Typography>

            <GuideRouter sections={sections} />

            {sections.map((s) => (
              <Box key={s.id} component="section">
                {/* id は見出しに置く。目次の scroll-spy が「薄い見出しが帯に
                  入ったか」で現在地を決めるため、節の箱には置けない */}
                <Typography
                  component="h2"
                  id={s.id}
                  sx={{
                    ...typeScale.h2,
                    mt: 8,
                    mb: 2,
                    scrollMarginTop: '32px',
                  }}
                >
                  {s.label ? `${s.label}. ${s.title}` : s.title}
                </Typography>
                {/* 連絡先は系統で違うため md ではなく variant が持つ。
                  「どこへ」(連絡先) → 「何を」(md の伝達項目) の順 */}
                {s.id === 'escalation' && contact}
                <MarkdownBody>{s.body}</MarkdownBody>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  )
}
